/**
 * 智能文案系统 - 主容器组件
 * 
 * 重构后结构：
 * - hooks.ts: 核心状态管理 (useSmartCopywriting)
 * - useStepHandlers.ts: 步骤处理逻辑
 * - useFormState.ts: 表单状态管理
 * - services.ts: API 调用
 * - components/: 各步骤的 UI 组件
 */

import React from 'react';
import { Card, Steps, Button, Result, Empty, Spin, App } from 'antd';
import {
    FileTextOutlined, TeamOutlined, EnvironmentOutlined,
    EditOutlined, CheckCircleOutlined, PlusOutlined,
} from '@ant-design/icons';

import { useSmartCopywriting } from './hooks';
import { useStepHandlers } from './useStepHandlers';
import { useSmartCopywritingForm } from './useFormState';
import { ProductInfoStep } from './components/ProductInfoStep';
import { AudienceStep } from './components/AudienceStep';
import { ScenarioStep } from './components/ScenarioStep';
import { CopyGenerationStep } from './components/CopyGenerationStep';
import type { SmartCopywritingProps } from './types';

import './styles.css';

// ========== 常量 ==========

const STEPS = [
    { key: 1, title: '产品信息', icon: <FileTextOutlined /> },
    { key: 2, title: '目标人群', icon: <TeamOutlined /> },
    { key: 3, title: '使用场景', icon: <EnvironmentOutlined /> },
    { key: 4, title: '文案生成', icon: <EditOutlined /> },
    { key: 5, title: '完成', icon: <CheckCircleOutlined /> },
];

// ========== 主组件 ==========

export const SmartCopywriting: React.FC<SmartCopywritingProps> = ({
    skuId,
    skuData,
    onProjectCreated,
    onDataChange,
}) => {
    const { message } = App.useApp();

    // 解决循环依赖：useFormState 需要 loadProject (来自 useSmartCopywriting)
    const loadProjectRef = React.useRef<((id: number) => Promise<void>) | null>(null);
    const safeLoadProject = React.useCallback((id: number) => {
        if (loadProjectRef.current) {
            loadProjectRef.current(id);
        }
    }, []);

    // 初始化表单和项目ID
    const {
        projectId,
        setProjectId,
        brands,
        productName,
        setProductName,
        sellingPoints,
        setSellingPoints,
        keywordsBySite,
        setKeywordsBySite,
        competitorTitles,
        setCompetitorTitles,
        selectedBrandId,
        setSelectedBrandId,
        selectedBrand,
        customAudienceInput,
        setCustomAudienceInput,
    } = useSmartCopywritingForm({
        skuId,
        skuData,
        loadProject: safeLoadProject,
        onDataChange,
    });

    // 核心业务逻辑
    const {
        project, audiences, scenarios, copyResult, currentStep, loading,
        selectedAudiences, selectedScenarios,
        loadProject, analyzeAudiences, selectAudiences, recommendScenarios,
        selectScenarios, generateCopy, saveVersion, goToStep, regenerateSingleFABE,
    } = useSmartCopywriting(projectId || undefined);

    // 更新 ref
    React.useEffect(() => {
        // @ts-ignore
        loadProjectRef.current = loadProject;
    }, [loadProject]);

    // ===== 步骤处理 =====
    const {
        handleStep1Next, handleReAnalyze, handleStep2Next,
        handleStep3Next, handleSaveVersion,
    } = useStepHandlers({
        productName,
        sellingPoints,
        keywordsBySite,
        competitorTitles,
        selectedBrandId,
        selectedBrand,
        customAudienceInput,
        projectId,
        project,
        selectedAudiencesCount: selectedAudiences.length,
        selectedScenariosCount: selectedScenarios.length,
        goToStep, analyzeAudiences, recommendScenarios, generateCopy, saveVersion,
        onProjectCreated,
        setProjectId,
    });

    // ===== 渲染步骤内容 =====
    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <ProductInfoStep
                        productName={productName}
                        sellingPoints={sellingPoints}
                        keywordsBySite={keywordsBySite}
                        competitorTitles={competitorTitles}
                        selectedBrand={selectedBrand}
                        brands={brands}
                        onProductNameChange={setProductName}
                        onSellingPointsChange={setSellingPoints}
                        onKeywordsBySiteChange={setKeywordsBySite}
                        onCompetitorTitlesChange={setCompetitorTitles}
                        onBrandChange={setSelectedBrandId}
                        onNext={handleStep1Next}
                        loading={loading.audiences}
                    />
                );
            case 2:
                return (
                    <AudienceStep
                        audiences={audiences}
                        onSelect={selectAudiences}
                        customInput={customAudienceInput}
                        onCustomInputChange={setCustomAudienceInput}
                        onAnalyze={handleReAnalyze}
                        onNext={handleStep2Next}
                        onBack={() => goToStep(1)}
                        loading={loading.audiences}
                        submitting={loading.scenarios}
                        analyzed={audiences.length > 0}
                    />
                );
            case 3:
                return (
                    <ScenarioStep
                        scenarios={scenarios}
                        onSelect={selectScenarios}
                        onNext={handleStep3Next}
                        onBack={() => goToStep(2)}
                        onRegenerate={recommendScenarios}
                        loading={loading.scenarios}
                        submitting={loading.generation}
                        keywordsBySite={keywordsBySite}
                    />
                );
            case 4:
                return (
                    <CopyGenerationStep
                        result={copyResult}
                        keywords={Object.values(keywordsBySite).flatMap(k => k.split(/[,，\n]+/).map(s => s.trim()).filter(Boolean))}
                        loading={loading.generation}
                        onSave={handleSaveVersion}
                        onBack={() => goToStep(3)}
                        onRegenerate={() => goToStep(3)}
                        onRegenerateFABE={regenerateSingleFABE}
                        saving={loading.saving}
                    />
                );
            case 5:
                return (
                    <Result
                        status="success"
                        title="文案生成完成！"
                        subTitle="您可以继续编辑或开始新的文案项目"
                        extra={[
                            <Button key="new" type="primary" icon={<PlusOutlined />} onClick={() => goToStep(1)}>
                                创建新文案
                            </Button>,
                            <Button key="edit" onClick={() => goToStep(4)}>
                                继续编辑
                            </Button>,
                        ]}
                    />
                );
            default:
                return <Empty description="未知步骤" />;
        }
    };

    if (loading.project) {
        return (
            <Card>
                <div style={{ textAlign: 'center', padding: 60 }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}>加载项目中...</div>
                </div>
            </Card>
        );
    }

    return (
        <div className="smart-copywriting">
            <Card bordered={false} style={{ marginBottom: 16 }}>
                <Steps
                    current={currentStep - 1}
                    items={STEPS.map(step => ({
                        key: step.key,
                        title: step.title,
                        icon: step.icon,
                    }))}
                />
            </Card>
            {renderStepContent()}
        </div>
    );
};

export default SmartCopywriting;
